/**
 * Checks if the JavaScript environment supports the `cause` option for Error objects.
 * 
 * This function attempts to create an Error with a cause. If successful, it returns true,
 * indicating that the environment supports ES2022 features. If an error is thrown during
 * the creation of the Error, it returns false, indicating that the `cause` option is not 
 * supported.
 * 
 * Potential alternative: Use polyfill https://github.com/ehmicky/error-cause-polyfill, but current check should be more lightweight
 * EXPORT: export was only added for unit testing purposes, not for production use
 * 
 * @returns {boolean} - Returns true if the `cause` option is supported, otherwise false.
 */
export const checkCause = (): boolean => {
  try {
    //@ts-ignore
    const errorWithCause = new Error("Test error", { cause: new Error("Cause error") });

    //@ts-ignore
    return !!errorWithCause.cause; // If the cause is correctly assigned, we have ES2022 support
  } catch (e) {
    return false; // If an error is thrown, the cause option is not supported
  }
};

/**
 * Checks if the cause is supported.
 * 
 * @returns {boolean} Returns true if the cause is supported, otherwise false.
 */
export const isCauseSupported = checkCause();

/**
 * Chains a custom error message with a provided error.
 *
 * This function creates a new Error object that includes a custom message
 * and, if supported, the original error as its cause. If the environment
 * does not support the cause feature (ES2022), it appends the stack of the
 * original error to the new error's stack.
 *
 * @param customErrorMessage - The custom error message to be associated with the new error.
 * @param error - The original error to be chained with the custom error message.
 * @returns A new Error object with the custom message and the original error's stack if applicable.
 */
export const chainError = (customErrorMessage: string, error: any) => {
  if (isCauseSupported) {
    //@ts-ignore
    return new Error(customErrorMessage, { cause: error }); // "cause" only works with ES2022, careful with tsconfig setup
  } else {
    const chainedError = new Error(customErrorMessage);
    chainedError.stack += `\nCaused by: ${error.stack}`;
    return chainedError;
  }
};